import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import styles from "../../styles/chronicOptionModals.module.css";
import { BASE_URL } from "../../utils/constants";
import axios from "axios";
import toast from "react-hot-toast";
import Spinner from "../../components/spinner";
import TagInput from "../../components/tagInput";
import NumberedTextArea from "../../components/patient-detail/patient-modals/numberedTextArea";
import {
  addChronicConditionOption,
  editChronicConditionOption,
  deleteChronicConditionOption,
} from "../../services/admin/chronicConditions";
import Button from "../../components/button-v1";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";

export const AddChronicConditionOption = ({ isOpen, onClose }) => {
  const { id: location_id } = useParams();
  const modalRef = useRef(null);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    icd_code: "",
    prognosis: "",
    symptoms: "",
    treatment_goals: "",
    planned_interventions: "",
  });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTagInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const mutation = useMutation(
    () => addChronicConditionOption(formData, location_id),
    {
      onSuccess: () => {
        toast.success("Condition Option added successfully");
        queryClient.invalidateQueries(["chronicConditionOptions"]);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "An error occurred.");
      },
    }
  );

  const validateForm = () => {
    let errors = [];

    if (!formData.name.trim()) errors.push("Name is required.");
    if (!formData.icd_code.trim()) errors.push("ICD Code is required.");
    if (!formData.prognosis.trim()) errors.push("Add atleast one Progonosis.");
    if (!formData.symptoms.trim()) errors.push("Add atleast one Sympton.");
    if (!formData.treatment_goals.trim())
      errors.push("Add atleast one Treatment Goal.");
    if (!formData.planned_interventions.trim())
      errors.push("Add atleast one Plan.");

    if (errors.length > 0) {
      toast.error(errors.join("\n"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line",
          textAlign: "left",
        },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      mutation.mutate();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Condition</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            className={`${styles.comp_wrapper} ${mutation.isLoading ? styles.loading : ""}`}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Chronic Condition</label>
                <input
                  type="text"
                  placeholder="Chronic Condition name"
                  value={formData.name}
                  name="name"
                  onChange={handleChange}
                />
              </div>

              <div className={styles.input_field}>
                <label>ICD Code</label>
                <input
                  type="text"
                  placeholder="ICD Code"
                  value={formData.icd_code}
                  name="icd_code"
                  onChange={handleChange}
                />
                {/* <select name="icd_code" value={formData.icd_code} onChange={handleChange}>
                                <option value="" disabled>
                                    Select ICD Code
                                </option>
                                {icdCodes.map((code) => (
                                    <option key={code.id} value={code.id}>
                                        {code.description} ({code.code})
                                    </option>
                                ))}
                            </select> */}
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <NumberedTextArea
                  label="Prognosis"
                  placeholder="Prognosis"
                  value={formData?.prognosis || ""}
                  onChange={(val) => {
                    setFormData({
                      ...formData,
                      prognosis: val,
                    });
                  }}
                  maxHeight={300}
                />
              </div>
              <div className={styles.input_field}>
                <NumberedTextArea
                  label="Symptoms"
                  placeholder="Symptoms"
                  value={formData?.symptoms || ""}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      symptoms: val,
                    }));
                  }}
                  maxHeight={300}
                />
              </div>
            </div>

            <div className={`${styles.input_group}`}>
              <div className={styles.input_field}>
                <NumberedTextArea
                  label="Treatment Goals"
                  placeholder="treatment_goals"
                  value={formData?.treatment_goals || ""}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      treatment_goals: val,
                    }));
                  }}
                  maxHeight={300}
                />
              </div>
              <div className={styles.input_field}>
                <NumberedTextArea
                  label="Planned Intervensions"
                  placeholder="planned_interventions"
                  value={formData?.planned_interventions || ""}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      planned_interventions: val,
                    }));
                  }}
                  maxHeight={300}
                />
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          {/* <div className={styles.submit_btn}> */}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            loading={mutation.isLoading}
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? "Adding..." : "Add"}
          </Button>{" "}
          {/* </div> */}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditChronicConditionOption = ({ data, isOpen, onClose }) => {
  const { id: location_id } = useParams();
  const modalRef = useRef(null);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: data?.name || "",
    icd_code: data?.icd_code || "",
    prognosis: data?.prognosis || "",
    symptoms: data?.symptoms || "",
    treatment_goals: data?.treatment_goals || "",
    planned_interventions: data?.planned_interventions || "",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        icd_code: data.icd_code || "",
        prognosis: data.prognosis || "",
        symptoms: data.symptoms || "",
        treatment_goals: data.treatment_goals || "",
        planned_interventions: data.planned_interventions || "",
      });
    }
  }, [data]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTagInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const mutation = useMutation(
    () =>
      editChronicConditionOption({ id: data.id, data: formData, location_id }),
    {
      onSuccess: () => {
        toast.success("Condition Option updated successfully");
        queryClient.invalidateQueries(["chronicConditionOptions"]);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "An error occurred.");
      },
    }
  );

  const validateForm = () => {
    let errors = [];

    if (!formData.name.trim()) errors.push("Name is required.");
    if (!formData.icd_code.trim()) errors.push("ICD Code is required.");
    if (!formData.prognosis.trim()) errors.push("Add atleast one Progonosis.");
    if (!formData.symptoms.trim()) errors.push("Add atleast one Sympton.");
    if (!formData.treatment_goals.trim())
      errors.push("Add atleast one Treatment Goal.");
    if (!formData.planned_interventions.trim())
      errors.push("Add atleast one Plan.");

    if (errors.length > 0) {
      toast.error(errors.join("\n"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line",
          textAlign: "left",
        },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      mutation.mutate();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.comp_header}>
          <p className={styles.comp_heading}>Edit Condition</p>
        </div>

        <form
          className={`${styles.comp_wrapper} ${mutation.isLoading ? styles.loading : ""
            }`}
        >
          <div className={styles.input_group}>
            <div className={styles.input_field}>
              <label>Chronic Condition</label>
              <input
                type="text"
                placeholder="Chronic Condition name"
                value={formData.name}
                name="name"
                onChange={handleChange}
              />
            </div>

            <div className={styles.input_field}>
              <label>ICD Code</label>
              <input
                type="text"
                placeholder="ICD Code"
                value={formData.icd_code}
                name="icd_code"
                onChange={handleChange}
              />
              {/* <select name="icd_code" value={formData.icd_code} onChange={handleChange}>
                                <option value="" disabled>
                                    Select ICD Code
                                </option>
                                {icdCodes.map((code) => (
                                    <option key={code.id} value={code.id}>
                                        {code.description} ({code.code})
                                    </option>
                                ))}
                            </select> */}
            </div>
          </div>

          <div className={styles.input_group}>
            <div className={styles.input_field}>
              {/* <label>Prognosis</label>
              <TagInput
                name={"prognosis"}
                onChange={handleTagInputChange}
                placeholder={"Add prognosis and press enter"}
                value={formData.prognosis}
              /> */}
              <NumberedTextArea
                label="Prognosis"
                placeholder="prognosis"
                value={formData?.prognosis || ""}
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    prognosis: val,
                  }));
                }}
                maxHeight={300}
              />
            </div>
            <div className={styles.input_field}>
              <NumberedTextArea
                label="Symptoms"
                placeholder="symptoms"
                value={formData?.symptoms || ""}
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    symptoms: val,
                  }));
                }}
                maxHeight={300}
              />
            </div>
          </div>

          <div className={`${styles.input_group}`}>
            <div className={styles.input_field}>
              <NumberedTextArea
                label="Treatment Goals"
                placeholder="treatment_goals"
                value={formData?.treatment_goals || ""}
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    treatment_goals: val,
                  }));
                }}
                maxHeight={300}
              />
            </div>
            <div className={styles.input_field}>
              <NumberedTextArea
                label="Planned Interventions"
                placeholder="planned_interventions"
                value={formData?.planned_interventions || ""}
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    planned_interventions: val,
                  }));
                }}
                maxHeight={300}
              />
            </div>
          </div>
        </form>

        <div className={styles.submit_btn}>
          {/* <button onClick={handleSubmit} disabled={mutation.isLoading}>
            Cancel
          </button> */}

          <button onClick={handleSubmit} disabled={mutation.isLoading}>
            {mutation.isLoading ? "Updating..." : "Update"}
          </button>
          {mutation.isLoading && <Spinner scale={5} />}
        </div>
      </div>
    </div>
  );
};

export const DeleteChronicCondition = ({ isOpen, onClose, data }) => {
  const { id: location_id } = useParams();
  const modalRef = useRef(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const mutation = useMutation(
    () => deleteChronicConditionOption(data.id, location_id),
    {
      onSuccess: () => {
        toast.success("Condition deleted successfully.");
        queryClient.invalidateQueries(["chronicConditionOptions"]);
        onClose();
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to delete condition."
        );
      },
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    mutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modal_2}`} ref={modalRef}>
        <div className={styles.comp_header}>
          <p className={styles.comp_heading}>Delete Condition</p>
        </div>

        <p className={styles.confirmation}>
          Are you sure you want to delete this condition <b>{data?.name}</b>?
        </p>

        <div className={`${styles.submit_btn} ${styles.submit_btn_2}`}>
          <button onClick={onClose} disabled={mutation.isLoading}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={mutation.isLoading}>
            {mutation.isLoading ? "Deleting..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};
